/**
 * ActionGroupDefault Component
 * @module views/modules/ActionGroupDefault
 */

import React from 'react';

import PropTypes from 'prop-types';
import { ActionGroupDefault as PalActionGroupDefault } from 'web-palette/dist/components/ActionGroupDefault/ActionGroupDefault';
import * as paletteUtils from 'web-palette/dist/components/ActionGroupDefault/ActionGroupDefault';
import { actionFormatter } from 'web-palette/dist/util/actionFormatter/actionFormatter';

import { useAuthContext } from '@ulta/core/providers/AuthProvider/AuthProvider';
import { AUTH0_SCREEN } from '@ulta/core/utils/auth/auth';
import { CLIENT_ACTION_TYPES } from '@ulta/core/utils/constants/action';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { getButtonHtml } from '@ulta/core/utils/getButtonHtml/getButtonHtml';
import { getLinkHtml } from '@ulta/core/utils/getLinkHtml/getLinkHtml';

import { isAuthButton } from '@ulta/utils/actionUtils/actionUtils';

/**
 * Represents a ActionGroup component
 *
 * @method
 * @param {ActionGroupProps} props - React properties passed from composition
 * @returns ActionGroup
 */
export const ActionGroupDefault = function( props ){
  const {
    // Action objects
    action1,
    action2,
    // Styles set in Amplience
    style,
    type,
    // Generated styles based coming from parent components
    derivedAriaLabel,
    fullyClickableParent,
    isKnockoutLink,
    // This is added to pass size to actionFormatter on palette; uses default size only for now but will derive the size from amplience in future
    size = defaultProps.size
  } = props;

  if( !action1 && !action2 ){
    return null;
  }

  let formattedAction1 = {};
  let formattedAction2 = {};
  const variant = paletteUtils.getVariant( type );
  const { getAuthUrl } = useAuthContext();

  if( isAuthButton( action1 ) ){
    let auth0Screen;
    if( action1.clientActionType === CLIENT_ACTION_TYPES.AUTH_SIGNUP ){
      auth0Screen = { screen: AUTH0_SCREEN.Signup };
    }
    action1.url = !isServer() && getAuthUrl( auth0Screen );
  }
  if( isAuthButton( action2 ) ){
    let auth0Screen;
    if( action2.clientActionType === CLIENT_ACTION_TYPES.AUTH_SIGNUP ){
      auth0Screen = { screen: AUTH0_SCREEN.Signup };
    }
    action2.url = !isServer() && getAuthUrl( auth0Screen );
  }
  if( !!action1 && action1.label ){
    formattedAction1 = actionFormatter(
      { action: action1, size: size, style: variant.action1, isKnockoutLink: isKnockoutLink, derivedAriaLabel: derivedAriaLabel, fullyClickableParent: fullyClickableParent },
      { getButtonMarkup: getButtonHtml, getLinkMarkup: getLinkHtml } );
  }

  if( !!action2 && action2.label ){
    formattedAction2 = actionFormatter(
      { action: action2, size: size, style: variant.action2, isKnockoutLink: isKnockoutLink, derivedAriaLabel: derivedAriaLabel, fullyClickableParent: fullyClickableParent },
      { getButtonMarkup: getButtonHtml, getLinkMarkup: getLinkHtml } );
  }

  return (
    <PalActionGroupDefault
      type={ type }
      style={ style }
      externalAction1={ formattedAction1 }
      externalAction2={ formattedAction2 }
      derivedAriaLabel={ derivedAriaLabel }
    />
  );
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} size='default' - Default size of the ActionGroupDefault
 */
export const defaultProps = {
  size: 'default'
};

/**
 * Property type definitions
 * @typedef ActionGroupDefaultProps
 * @type {object}
 * @property {string} example - refactor or delete
 */
export const propTypes =  {
  /**
   * Sets the stylistic version of the actions
   * - **Primary Button/Secondary Button:** Sets the actions to display as the Primary Button and Secondary Button
   * - **Primary Button/Tertiary Button:** Sets the actions to display as the Primary Button and Tertiary Button
   * - **Primary Button/Primary Link:** Sets the actions to display as the Primary Button and Primary Link
   * - **Secondary Button/Primary Link:** Sets the actions to display as the Secondary Button and Primary Link
   * - **Primary Link/Primary Link:** Sets the actions to display as two Primary Links
   * - **Arrow Link:** Sets the link to a arrow style
   * - **Secondary Button/Primary Button:** Sets the actions to display as the Seonday Button and Primary Button
   * - **Tertiary Button/Primary Button:** Sets the actions to display as the Tertiary Button and Primary Button
   * - **Primary Link/Primary Button:** Sets the actions to display as the Primary Link and Primary Button
   * - **Primary Link/Secondary Button:** Sets the actions to display as the Primary Link and Secondary Button
   * - **Primary Inverse Button/Primary Link:** Sets the actions to display as the Primary Inverse Button and Primary Link
   * - **Primary Link/Primary Inverse Button:** Sets the actions to display as the Primary Link and Primary Inverse Button
   * - **Primary Button/Primary Inverse Button:** Sets the actions to display as the Primary Button and Primary Inverse Button
   * - **Primary Inverse Button/Primary Button:** Sets the actions to display as the Primary Inverse Button and Primary Button
   * - **AI Button:** Sets the actions to display as the AI Button
   */
  type: PropTypes.oneOf( [
    'Primary Button/Secondary Button',
    'Primary Button/Tertiary Button',
    'Primary Button/Primary Link',
    'Secondary Button/Primary Link',
    'Primary Link/Primary Link',
    'Arrow Link',
    'Secondary Button/Primary Button',
    'Tertiary Button/Primary Button',
    'Primary Link/Primary Button',
    'Primary Link/Secondary Button',
    'Primary Inverse Button/Primary Link',
    'Primary Link/Primary Inverse Button',
    'Primary Button/Primary Inverse Button',
    'Primary Inverse Button/Primary Button',
    'AI Button'
  ] ),
  /**
   * Sets the stylistic version of the action
   * - **inline:** the actions are displayed in a row
   * - **stacked:** the actions are displayed in a column
   * - **stackedFullWidth:** the actions are displayed in a column- buttons are full width and links are centered.
   */
  style: PropTypes.oneOf( [
    'inline',
    'stackedDefault',
    'stackedFullWidth',
    'inlineFullWidth'
  ] ),
  /**
   * Sets the primary action (palette only, no business logic)
   */
  action1: PropTypes.object,
  /**
   * Sets the secondary action (palette only, no business logic)
   */
  action2: PropTypes.object,
  /**
   * Boolean if link text is white
   */
  isKnockoutLink: PropTypes.bool,
  /**
   * Sets the size of actions inside ActionGroupDefault ( palette only prop and needs to come from dxl )
   */
  size: PropTypes.string
};


ActionGroupDefault.propTypes = propTypes;
ActionGroupDefault.defaultProps = defaultProps;

export default ActionGroupDefault;